import React, { useMemo, useEffect, useState, useCallback } from 'react';
/**
 * ReferralInitiation (unified pre-account wizard)
 * ------------------------------------------------
 * This is the main referral form component that implements the "Digital Front Door"
 * / single point of access approach. Instead of having separate flows for different
 * referrer types, this single form uses branching questions to determine the pathway.
 * 
 * Supported Pathways (determined by answers):
 *   - CYP 1.1: Legal Guardian (parent, step-parent, foster carer, etc.) → CYP referral
 *   - CYP 1.2: School Representative → CYP referral
 * 
 * Not Yet Supported (disabled in UI):
 *   - Self-referrals (CYP 1.4, A 1.1)
 *   - Adult referrals (A 1.2 - A 1.5)
 * 
 * Flow:
 *   1. Referrer details (name, email, phone)
 *   2. "Who is this referral for?" → Myself (disabled) / Someone else
 *   3. "Adult or CYP?" → Adult (disabled) / CYP
 *   4. Subject's home address (postcode eligibility check)
 *   5. Relationship to CYP (determines pathway)
 *   6. CYP details (name, DOB)
 *   7. Review and submit
 * 
 * Technical notes:
 *   - Uses referralInitiationUnified schema with visibleWhen conditions
 *   - Autosaves per-step; submits intent; routes to outcome
 *   - Pathway is computed from answers at submission time
 */
import { getOrgConfig } from '../../utils/orgResolution';
import RmsFormTemplate from '../../components/RmsFormTemplate';
import { useReferralWizardState } from '../../hooks/useReferralWizardState';
import { apiSaveIntentDraft, apiSubmitIntent, apiCreateIntent, apiCheckEmailAvailable } from '../../utils/rmsApi';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { generateIdempotencyKey } from '../../utils/idempotency';
import useRmsAnalytics from '../../hooks/useRmsAnalytics';
import { apiCheckPostcodeEligibility } from '../../utils/rmsApi';
import { useRmsState } from '../../context/RmsStateContext';
import { CONSENT_NOT_GIVEN } from '../../constants/outcomeCopy';
import { isPublicReferralFormClosed } from '../../constants/orgFeatures';

export default function ReferralInitiation() {
  const { orgId } = useMemo(() => getOrgConfig(), []);
  if (isPublicReferralFormClosed(orgId)) {
    return <Navigate to="/rms/school-sign-in" replace />;
  }
  return <ReferralInitiationForm />;
}

function ReferralInitiationForm() {
  const { orgId } = useMemo(() => getOrgConfig(), []);
  // Use the new unified schema that handles all pathways via branching
  const formKey = 'referralInitiationUnified';
  const navigate = useNavigate();
  const location = useLocation();
  // Destructure stable useCallback references to avoid re-running init effects
  // on every context version bump (see RmsStateContext versionTick).
  const { getCachedSchema, ensurePreIntentBundle, ensurePreSchema } = useRmsState();
  
  // Initialize schema with cached value if available for instant render
  // This prevents "Loading..." flash when returning from email-in-use page
  const [schema, setSchema] = useState(() => {
    try {
      return getCachedSchema(orgId, formKey) || null;
    } catch (_) {
      return null;
    }
  });
  
  const [prospect, setProspect] = useState({ prospectId: null, clientToken: null });
  const wizard = useReferralWizardState(schema || { sections: [] });
  const analytics = useRmsAnalytics(orgId);
  const [initialStepId, setInitialStepId] = useState(null);
  const { setAllAnswers } = wizard;

  // Compute pathway indicator from answers for analytics/logging
  // (actual pathway determination happens server-side at submit)
  const computedPathway = useMemo(() => {
    const answers = wizard.answers || {};
    if (answers.referral_for !== 'someone_else') return null;
    if (answers.subject_age_group !== 'cyp') return null;
    
    const relationship = answers.relationship_cyp;
    if (!relationship) return null;
    
    // School representative → CYP 1.2
    if (relationship === 'school_rep') return 'CYP_1.2';
    
    // All guardian-type relationships → CYP 1.1
    const guardianTypes = ['parent', 'step_parent', 'foster_carer', 'kinship_carer', 'legal_guardian', 'informal_carer'];
    if (guardianTypes.includes(relationship)) return 'CYP_1.1';
    
    return null;
  }, [wizard.answers]);

  // Form context for schema evaluation (variants, optionRefs)
  // With the unified schema, we no longer need initiatedBy from URL
  const formContext = useMemo(() => ({
    pathway: 'unified',
    currentUser: { role: 'anonymous_preaccount' },
    // Computed pathway for any context-aware UI
    computedPathway
  }), [computedPathway]);

  // Initialize schema; resume intent when intentId is present
  // Also handles clearEmail flag for returning from email-in-use page
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1) Try context cache for instant render (prefetched by start page)
        const cachedSchema = getCachedSchema(orgId, formKey);
        if (cachedSchema) setSchema(cachedSchema);
        const params = new URLSearchParams(location.search);
        const intentId = params.get('intentId');
        // clientToken is required when resuming an intent (passed from email-in-use return URL)
        const clientToken = params.get('clientToken');
        
        // Check if returning from email-in-use page (need to clear email after loading)
        const shouldClearEmail = params.get('clearEmail') === 'true';
        
        // Helper to clean up sensitive URL params after successful loading
        const cleanupUrl = () => {
          // Remove sensitive params from URL for security and to prevent issues on refresh
          params.delete('clientToken');
          params.delete('clearEmail');
          const newSearch = params.toString();
          const newUrl = newSearch 
            ? `${location.pathname}?${newSearch}`
            : location.pathname;
          window.history.replaceState({}, '', newUrl);
        };
        
        // Helper to handle clearEmail after answers are loaded
        const handleClearEmailIfNeeded = (loadedAnswers) => {
          // Always clean up URL (remove clientToken and clearEmail)
          cleanupUrl();
          
          if (!shouldClearEmail) return loadedAnswers;
          
          // Clear email fields from the loaded answers
          const clearedAnswers = { ...loadedAnswers };
          delete clearedAnswers.ref_email;
          delete clearedAnswers.ref_email_confirm;
          
          // Set the initial step to the email group so user lands on the email input
          setInitialStepId('ref_email_group');
          
          return clearedAnswers;
        };
        
        // 1a) Resume via URL param (intentId + clientToken for authenticated resume)
        if (intentId) {
          const bundle = await ensurePreIntentBundle({ orgId, formKey, intentId, clientToken, createIfMissing: false });
          if (cancelled) return;
          setSchema(bundle?.schema || cachedSchema || null);
          setProspect({ prospectId: bundle?.prospectId || intentId, clientToken: bundle?.clientToken || clientToken });
          
          // Apply clearEmail logic after loading answers
          const finalAnswers = handleClearEmailIfNeeded(bundle?.answers || {});
          setAllAnswers(finalAnswers);
          
          if (!shouldClearEmail && bundle?.initialStepId) setInitialStepId(bundle.initialStepId);
          analytics.intentStarted({ formKey, prospectId: bundle?.prospectId, resumed: true });
        } else {
          // 1b) Safer resume via localStorage (unified key without initiatedBy)
          let resumeProspectId = null;
          try {
            const resumeKey = `rms:preintent:${orgId}:${formKey}`;
            const val = window.localStorage.getItem(resumeKey);
            resumeProspectId = val || null;
          } catch (_) {}
          
          if (resumeProspectId) {
            try {
              const bundle = await ensurePreIntentBundle({ orgId, formKey, intentId: resumeProspectId, createIfMissing: false });
              if (!cancelled && bundle) {
                setSchema(bundle?.schema || cachedSchema || null);
                setProspect({ prospectId: bundle?.prospectId || resumeProspectId, clientToken: bundle?.clientToken || resumeProspectId });
                
                // Apply clearEmail logic after loading answers
                const finalAnswers = handleClearEmailIfNeeded(bundle?.answers || {});
                setAllAnswers(finalAnswers);
                
                if (!shouldClearEmail && bundle?.initialStepId) setInitialStepId(bundle.initialStepId);
                analytics.intentStarted({ formKey, prospectId: bundle?.prospectId || resumeProspectId, resumed: true });
                return;
              }
            } catch (_) {
              // Fallback to schema-only if resume fails
            }
          }
          
          // New flow: load schema only; defer prospect creation until first save
          const sch = await ensurePreSchema({ orgId, formKey, force: false });
          if (cancelled) return;
          setSchema(sch || cachedSchema || null);
          
          // Handle clearEmail even for new flows (edge case)
          if (shouldClearEmail) {
            handleClearEmailIfNeeded({});
          }
        }
      } catch (e) {
        console.error('RMS init failed', e);
        analytics.error({ where: 'init', message: String(e?.message || e) });
      }
    })();
    return () => { cancelled = true }
  }, [orgId, analytics, formKey, location.search, location.pathname, setAllAnswers, getCachedSchema, ensurePreIntentBundle, ensurePreSchema]);

  // Handle saving each step (creates prospect on first save if needed)
  const handleSaveStep = useCallback(async (currentStep, stepPatch) => {
    try {
      let prospectId = prospect.prospectId;
      let clientToken = prospect.clientToken;
      
      // Create prospect on first save if missing
      if (!prospectId) {
        const idemStoreKey = `rms:preintent:idempotency:${orgId}:${formKey}`;
        let idemKey = null;
        try {
          idemKey = window.localStorage.getItem(idemStoreKey);
          if (!idemKey) {
            idemKey = generateIdempotencyKey('rms-pre');
            window.localStorage.setItem(idemStoreKey, idemKey);
          }
        } catch (_) {}
        
        const created = await apiCreateIntent({
          orgId,
          formKey,
          idempotencyKey: idemKey || generateIdempotencyKey('rms'),
          // No initiatedBy param - pathway determined by answers
        });
        
        prospectId = created?.prospectId || null;
        clientToken = created?.clientToken || null;
        
        if (created?.schema && !schema) {
          setSchema(created.schema);
        }
        setProspect({ prospectId, clientToken });
        
        // Persist resume pointer (without initiatedBy suffix)
        try {
          const resumeKey = `rms:preintent:${orgId}:${formKey}`;
          if (prospectId) window.localStorage.setItem(resumeKey, prospectId);
        } catch (_) {}
        
        analytics.intentStarted({ formKey, prospectId });
      }
      
      await apiSaveIntentDraft({
        orgId,
        prospectId,
        clientToken,
        formKey,
        patch: stepPatch,
        stepId: currentStep?.questionId
      });
      
      analytics.stepSaved({ stepId: currentStep?.questionId, prospectId, pathway: computedPathway });
    } catch (e) {
      console.error('Draft save failed', e);
      analytics.error({ where: 'saveStep', stepId: currentStep?.questionId, message: String(e?.message || e) });
    }
  }, [orgId, prospect.prospectId, prospect.clientToken, analytics, formKey, schema, computedPathway]);

  // Normalize UK postcode for eligibility checking
  function normalizeUkPostcode(raw) {
    if (!raw) return '';
    let s = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (s.length > 3) {
      s = s.slice(0, -3) + ' ' + s.slice(-3);
    }
    return s.trim();
  }

  // Loading state
  if (!schema) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <div className="text-gray-600">Loading referral form...</div>
      </div>
    );
  }

  return (
    <RmsFormTemplate
      schema={schema}
      answers={wizard.answers}
      setAnswer={wizard.setAnswer}
      initialStepId={initialStepId}
      onSaveStep={handleSaveStep}
      onComplete={async () => {
        if (!prospect.prospectId) return;
        try {
          analytics.intentSubmitted({ prospectId: prospect.prospectId, formKey, pathway: computedPathway });
          
          console.log('[RMS] Submitting intent…', { orgId, prospectId: prospect.prospectId, formKey, pathway: computedPathway });
          
          // Final defensive sync before submit.
          // The referral form is now school-rep-only: the "who is this referral
          // for", adult/CYP and relationship questions are hidden, so seed the
          // answers they used to provide. These defaults only fill gaps — any
          // real answer in wizard.answers takes precedence — and guarantee the
          // server routes to the school-rep CYP pathway (relationship_cyp).
          try {
            const schoolRepDefaults = {
              referral_for: 'someone_else',
              subject_age_group: 'cyp',
              relationship_cyp: 'school_rep',
            };
            await apiSaveIntentDraft({
              orgId,
              prospectId: prospect.prospectId,
              clientToken: prospect.clientToken,
              formKey,
              patch: { ...schoolRepDefaults, ...(wizard?.answers || {}) },
              stepId: '__final_sync__'
            });
          } catch (e) {
            console.warn('[RMS] Final sync before submit failed', e);
          }
          
          const { decision, reasonCode } = await apiSubmitIntent({
            orgId,
            prospectId: prospect.prospectId,
            clientToken: prospect.clientToken,
            formKey,
            idempotencyKey: generateIdempotencyKey('submit')
          });
          
          console.log('[RMS] Submit response', { decision, reasonCode });
          
          // Store email for sign-in flow
          try {
            const email = wizard?.answers?.ref_email;
            if (email) window.localStorage.setItem('emailForSignIn', email);
          } catch (_) {}
          
          // Clear idempotency key after successful submit
          try {
            const idemStoreKey = `rms:preintent:idempotency:${orgId}:${formKey}`;
            window.localStorage.removeItem(idemStoreKey);
          } catch (_) {}
          
          // Clear resume pointer
          try {
            const resumeKey = `rms:preintent:${orgId}:${formKey}`;
            window.localStorage.removeItem(resumeKey);
          } catch (_) {}
          
          // formKey is required on the first-party rms_eligibility_checked event:
          // without it the submit-time outcome is dropped before it leaves the app.
          if (decision === 'eligible') {
            analytics.eligibilityPassed({ formKey, prospectId: prospect.prospectId, reason: reasonCode || null, pathway: computedPathway });
          } else {
            analytics.eligibilityFailed({ formKey, prospectId: prospect.prospectId, reason: reasonCode || null, pathway: computedPathway });
          }
          
          navigate(`/rms/referral/outcome?decision=${encodeURIComponent(decision)}${reasonCode ? `&reason=${encodeURIComponent(reasonCode)}` : ''}`);
        } catch (e) {
          console.error('Submit failed', e, e?.details);
          analytics.error({ where: 'submit', message: String(e?.message || e), pathway: computedPathway });
        }
      }}
      formContext={formContext}
      beforeNext={async (currentStep, currentAnswers) => {
        // ─────────────────────────────────────────────────────────────────────
        // Consent gate: a referral cannot proceed without consent.
        // Stopped at the question itself, before any of the child's details
        // are asked for, so nothing about them is ever entered or stored.
        // rmsSubmitIntent repeats the check server-side.
        // ─────────────────────────────────────────────────────────────────────
        if (currentStep?.questionId === 'consent_confirmed') {
          const answer = currentAnswers?.consent_confirmed;
          const optionId = typeof answer === 'string' ? answer : answer?.optionId;
          if (optionId && optionId !== 'yes') {
            if (typeof analytics.eligibilityFailed === 'function') {
              analytics.eligibilityFailed({
                formKey,
                prospectId: prospect.prospectId,
                reason: CONSENT_NOT_GIVEN,
                pathway: computedPathway,
              });
            }
            navigate(`/rms/referral/outcome?decision=ineligible&reason=${CONSENT_NOT_GIVEN}`);
            return { proceed: false };
          }
        }

        // ─────────────────────────────────────────────────────────────────────
        // Email availability check: Prevent users with existing accounts from
        // creating duplicate referrals - they should sign in instead
        // ─────────────────────────────────────────────────────────────────────
        if (currentStep?.questionId === 'ref_email_group') {
          try {
            const email = (currentAnswers?.ref_email || '').trim().toLowerCase();
            if (email && email.includes('@')) {
              const result = await apiCheckEmailAvailable({ orgId, email });
              
              if (result?.hasAccount) {
                // Email is already in use - navigate to dedicated page instead of browser dialog
                // Build returnTo URL with intentId and clientToken for seamless resume when user comes back
                // The clientToken is required by the API to authenticate the intent state request
                let returnTo = `/rms/referral/init?orgId=${encodeURIComponent(orgId)}`;
                if (prospect.prospectId) {
                  returnTo += `&intentId=${encodeURIComponent(prospect.prospectId)}`;
                }
                if (prospect.clientToken) {
                  returnTo += `&clientToken=${encodeURIComponent(prospect.clientToken)}`;
                }
                
                navigate(`/rms/referral/email-in-use?email=${encodeURIComponent(email)}&returnTo=${encodeURIComponent(returnTo)}`);
                return { proceed: false };
              }
            }
          } catch (e) {
            // Don't block the form if email check fails - log and continue
            console.warn('[ReferralInitiation] Email availability check failed:', e);
            if (typeof analytics.error === 'function') {
              analytics.error({ where: 'email_check', message: String(e?.message || e) });
            }
          }
          return { proceed: true };
        }
        
        // ─────────────────────────────────────────────────────────────────────
        // Postcode eligibility check after address step
        // ─────────────────────────────────────────────────────────────────────
        if (currentStep?.questionId === 'subject_address_group') {
          try {
            const rawPostcode = currentAnswers?.addr_postcode || '';
            const normalized = normalizeUkPostcode(rawPostcode);
            
            if (typeof analytics.eligibilityChecked === 'function') {
              const outward = normalized.split(' ')[0] || null;
              analytics.eligibilityChecked({ formKey, pathway: computedPathway, outwardCode: outward });
            }
            
            const res = await apiCheckPostcodeEligibility({
              orgId,
              prospectId: prospect.prospectId || undefined,
              postcodeRaw: rawPostcode,
              postcodeNormalized: normalized,
              usedPostcodeType: 'child_home'
            });
            
            console.log('Eligibility check result:', res);
            
            if (res?.eligible === false) {
              if (typeof analytics.eligibilityFailed === 'function') {
                analytics.eligibilityFailed({ formKey, pathway: computedPathway, reason: res?.reasonCode || null, areaVersion: res?.areaVersion || null });
              }
              navigate(`/rms/referral/outcome?decision=ineligible${res?.reasonCode ? `&reason=${encodeURIComponent(res.reasonCode)}` : ''}`);
              return { proceed: false };
            }
            
            if (typeof analytics.eligibilityPassed === 'function') {
              analytics.eligibilityPassed({ formKey, pathway: computedPathway, areaVersion: res?.areaVersion || null });
            }
            return { proceed: true };
          } catch (e) {
            if (typeof analytics.error === 'function') {
              analytics.error({ where: 'eligibility_check', message: String(e?.message || e), pathway: computedPathway });
            }
            throw e;
          }
        }
        return { proceed: true };
      }}
    />
  );
}
